import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Skeleton } from "../../Components/Needed";
import { fetchMessages } from "../../Helpers/campaignApi";
import TemplateEditor from "../../Components/TemplateEditor";
import { CHANNEL_LABEL } from "./shared";

const STATE = {
    custom: { text: "Written", cls: "bg-success-subtle text-success" },
    library: { text: "Library", cls: "bg-info-subtle text-info-emphasis" },
    off: { text: "Off", cls: "bg-secondary-subtle text-secondary" },
    missing: { text: "Not written", cls: "bg-warning-subtle text-warning-emphasis" },
};

// The campaign's messages: one per channel and language. Click a cell to write it, or to use a Library
// template (the shared template editor).
// Other languages fall back to the first one, so only the first language must be written.
const MessagesPanel = ({ campaignId, onChange }) => {
    const [grid, setGrid] = useState(null);
    const [open, setOpen] = useState(null); // { channel, language }

    const load = useCallback(async () => {
        const result = await fetchMessages(campaignId);
        if (!result.ok) { toast.error(result.message); return; }
        setGrid(result.data);
        onChange?.(result.data);
    }, [campaignId]); // eslint-disable-line
    useEffect(() => { load(); }, [load]);

    if (open) {
        return (
            <TemplateEditor
                target={{ kind: "campaign", campaignId, channel: open.channel, language: open.language }}
                title={`${CHANNEL_LABEL[open.channel] || open.channel} message`}
                subtitle={`Language: ${open.language}`}
                onClose={() => { setOpen(null); load(); }}
                onSaved={load}
            />
        );
    }
    if (!grid) return <Skeleton height={160} />;

    return (
        <div>
            <div className="table-responsive">
                <table className="table table-sm align-middle">
                    <thead>
                        <tr>
                            <th scope="col">Channel</th>
                            {grid.languages.map((lang) => <th scope="col" key={lang}>{lang}{lang === grid.languages[0] ? " (main)" : ""}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {grid.channels.map((channel) => (
                            <tr key={channel}>
                                <th scope="row" className="fw-normal">{CHANNEL_LABEL[channel]}</th>
                                {grid.languages.map((language) => {
                                    const cell = grid.cells.find((c) => c.channel === channel && c.language === language) || { state: "missing", problems: [] };
                                    const look = STATE[cell.state] || STATE.missing;
                                    return (
                                        <td key={language}>
                                            <button type="button" className="btn btn-link btn-sm p-0 text-start text-decoration-none" onClick={() => setOpen({ channel, language })}>
                                                <span className={`nc-status ${look.cls}`}>{look.text}</span>
                                                {cell.version ? <span className="nc-sub ms-1">v{cell.version}</span> : null}
                                                {cell.libraryName ? <span className="nc-sub ms-1">{cell.libraryName}</span> : null}
                                            </button>
                                            {cell.problems.map((p) => <div key={p} className="small text-danger">{p}</div>)}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {grid.problems.length === 0
                ? <div className="small text-success">Every message needed is written.</div>
                : <div className="small text-muted">{grid.problems.length} thing{grid.problems.length === 1 ? "" : "s"} to fix before the campaign can start.</div>}
        </div>
    );
};

export default MessagesPanel;
